<template>
  <validation-observer ref="shopObserver">
    <b-form @submit.prevent="validationForm">
      <!-- Header Section -->
      <div class="mb-3">
        <div class="card">
          <div
            class="card-header d-flex justify-content-between align-items-center"
            style="background-color: rgba(115, 103, 240, 0.12)">
            <h4 class="mb-0">
              <feather-icon icon="MapPinIcon" size="22" class="mr-1" />
              <span>Sklep {{ shop.name }}</span>
            </h4>
          </div>
        </div>
      </div>

      <!-- Main Form Section -->
      <div class="card">
        <div class="card-body">
          <!-- Location Information -->
          <h5 class="mb-2 d-flex align-items-center">
            <feather-icon icon="InfoIcon" size="18" class="mr-1" />
            Informacje podstawowe
          </h5>
          <b-row>
            <b-col cols="12" md="6">
              <validation-provider #default="{ errors }" name="Nazwa">
                <b-form-group label="Nazwa" label-for="name">
                  <b-form-input
                    id="name"
                    v-model="form.name"
                    :state="errors.length > 0 ? false : null"
                    placeholder="Wprowadź nazwę sklepu" />
                  <small class="text-danger">{{ errors[0] }}</small>
                </b-form-group>
              </validation-provider>
            </b-col>

            <b-col cols="12" md="6">
              <validation-provider #default="{ errors }" name="Kraj">
                <b-form-group label="Kraj" label-for="country">
                  <select
                    id="country"
                    v-model="form.country"
                    class="form-control"
                    :class="{ 'is-invalid': errors.length > 0 }">
                    <option disabled value="null">Wybierz kraj</option>
                    <option value="PL">Polska</option>
                    <option value="UA">Ukraina</option>
                  </select>
                  <small class="text-danger">{{ errors[0] }}</small>
                </b-form-group>
              </validation-provider>
            </b-col>

            <b-col cols="12" md="12">
              <b-form-group label="Nadpisuj dane z TETA?" label-for="should_update">
                <b-form-checkbox id="should_update" v-model="form.should_update" name="should_update" switch>
                  {{ form.should_update ? 'Tak' : 'Nie' }}
                </b-form-checkbox>
              </b-form-group>

              <b-form-group label="Sklep online?" label-for="is_online">
                <b-form-checkbox id="is_online" v-model="form.is_online" name="is_online" switch>
                  {{ form.is_online ? 'Tak' : 'Nie' }}
                </b-form-checkbox>
              </b-form-group>

              <b-form-group label="Dystrybutor oficjalny?" label-for="is_official">
                <b-form-checkbox id="is_official" v-model="form.is_official" name="is_official" switch>
                  {{ form.is_official ? 'Tak' : 'Nie' }}
                </b-form-checkbox>
              </b-form-group>
            </b-col>

            <b-col cols="12" md="6">
              <validation-provider #default="{ errors }" name="Województwo">
                <b-form-group label="Powiat" label-for="county">
                  <b-form-input id="county" v-model="form.county" :state="errors.length > 0 ? false : null" />
                  <small class="text-danger">{{ errors[0] }}</small>
                </b-form-group>
              </validation-provider>
            </b-col>

            <b-col cols="12" md="6">
              <validation-provider #default="{ errors }" name="Kategoria">
                <b-form-group label="Kategoria" label-for="category">
                  <div class="d-flex align-items-center justify-content-between">
                    <b-form-input
                      id="category"
                      v-model="form.category"
                      :state="errors.length > 0 ? false : null"
                      placeholder="Wprowadź kategorię" />
                    <b-button variant="outline-secondary" class="ml-2" @click="showCategoryModal" type="button">
                      <feather-icon icon="AlertCircleIcon" size="14" />
                    </b-button>
                  </div>
                  <small class="text-danger">{{ errors[0] }}</small>
                </b-form-group>
              </validation-provider>
            </b-col>
          </b-row>

          <!-- Address Section -->
          <h5 class="mb-2 d-flex align-items-center mt-2">
            <feather-icon icon="HomeIcon" size="18" class="mr-1" />
            Adres
          </h5>
          <b-row>
            <b-col cols="12" md="6">
              <validation-provider #default="{ errors }" name="Miasto">
                <b-form-group label="Miasto" label-for="address_city">
                  <b-form-input
                    id="address_city"
                    v-model="form.address_city"
                    :state="errors.length > 0 ? false : null"
                    placeholder="Wprowadź miasto" />
                  <small class="text-danger">{{ errors[0] }}</small>
                </b-form-group>
              </validation-provider>
            </b-col>

            <b-col cols="12" md="6">
              <validation-provider #default="{ errors }" name="Ulica">
                <b-form-group label="Ulica" label-for="address_street">
                  <b-form-input
                    id="address_street"
                    v-model="form.address_street"
                    :state="errors.length > 0 ? false : null"
                    placeholder="Wprowadź ulicę" />
                  <small class="text-danger">{{ errors[0] }}</small>
                </b-form-group>
              </validation-provider>
            </b-col>

            <b-col cols="12" md="4">
              <validation-provider #default="{ errors }" name="Numer budynku">
                <b-form-group label="Numer budynku" label-for="address_number">
                  <b-form-input
                    id="address_number"
                    v-model="form.address_number"
                    :state="errors.length > 0 ? false : null"
                    placeholder="Wprowadź numer budynku" />
                  <small class="text-danger">{{ errors[0] }}</small>
                </b-form-group>
              </validation-provider>
            </b-col>

            <b-col cols="12" md="4">
              <validation-provider #default="{ errors }" name="Numer lokalu">
                <b-form-group label="Numer lokalu" label-for="address_local_number">
                  <b-form-input
                    id="address_local_number"
                    v-model="form.address_local_number"
                    :state="errors.length > 0 ? false : null"
                    placeholder="Wprowadź numer lokalu" />
                  <small class="text-danger">{{ errors[0] }}</small>
                </b-form-group>
              </validation-provider>
            </b-col>

            <b-col cols="12" md="4">
              <validation-provider #default="{ errors }" name="Kod pocztowy">
                <b-form-group label="Kod pocztowy" label-for="postal_code">
                  <b-form-input
                    id="postal_code"
                    v-model="form.postal_code"
                    :state="errors.length > 0 ? false : null"
                    placeholder="Wprowadź kod pocztowy" />
                  <small class="text-danger">{{ errors[0] }}</small>
                </b-form-group>
              </validation-provider>
            </b-col>

            <b-col cols="12" md="4">
              <validation-provider #default="{ errors }" name="Link">
                <b-form-group label="Link" label-for="link">
                  <b-form-input
                    id="link"
                    v-model="form.link_1"
                    :state="errors.length > 0 ? false : null"
                    placeholder="Wprowadź link" />
                  <small class="text-danger">{{ errors[0] }}</small>
                </b-form-group>
              </validation-provider>
            </b-col>
          </b-row>

          <!-- Geo Location Section -->
          <h5 class="mb-2 d-flex align-items-center mt-2">
            <feather-icon icon="MapIcon" size="18" class="mr-1" />
            Lokalizacja geograficzna
            <a v-if="form.latitude && form.longitude" class="ml-2" :href="`https://www.google.com/maps/place/${form.latitude},${form.longitude}`"
                target="_blank"
                rel="noopener noreferrer">
                zobacz na Google Maps
            </a>
          </h5>
          <b-row>
            <b-col cols="12" md="6">
              <validation-provider #default="{ errors }" name="Szerokość geograficzna">
                <b-form-group label="Szerokość geograficzna" label-for="latitude">
                  <b-form-input
                    id="latitude"
                    v-model="form.latitude"
                    :state="errors.length > 0 ? false : null"
                    placeholder="Wprowadź szerokość geograficzną"
                    type="number"
                    step="0.00000001" />
                  <small class="text-danger">{{ errors[0] }}</small>
                </b-form-group>
              </validation-provider>
            </b-col>

            <b-col cols="12" md="6">
              <validation-provider #default="{ errors }" name="Długość geograficzna">
                <b-form-group label="Długość geograficzna" label-for="longitude">
                  <b-form-input
                    id="longitude"
                    v-model="form.longitude"
                    :state="errors.length > 0 ? false : null"
                    placeholder="Wprowadź długość geograficzną"
                    type="number"
                    step="0.00000001" />
                  <small class="text-danger">{{ errors[0] }}</small>
                </b-form-group>
              </validation-provider>
            </b-col>
          </b-row>

          <!-- Submit Button -->
          <div class="mt-4 d-flex justify-content-end">
            <b-button to="/shops" variant="outline-secondary" class="mr-2">
              <feather-icon icon="XIcon" size="16" class="mr-1" />
              Anuluj
            </b-button>
            <b-button type="button" variant="outline-secondary" class="mr-2" @click="tryToGetLocation">
              <feather-icon icon="MapIcon" size="16" class="mr-1" />
              Spróbuj pobrać lokalizację
            </b-button>
            
            <b-button v-ripple.400="'rgba(255, 255, 255, 0.15)'" type="submit" variant="primary">
              <feather-icon icon="SaveIcon" size="16" class="mr-1" />
              Zapisz
            </b-button>
          </div>
        </div>
      </div>
    </b-form>
  </validation-observer>
</template>

<script>
import NotificationMixin from '@/mixins/NotificationMixin';
import { ValidationProvider, ValidationObserver } from 'vee-validate';
import { BButton, BForm, BFormInput, BFormGroup, BRow, BCol } from 'bootstrap-vue';
import { getLocation } from '@/http/shops';

export default {
  components: {
    ValidationProvider,
    ValidationObserver,
    BButton,
    BForm,
    BFormInput,
    BFormGroup,
    BRow,
    BCol,
  },
  mixins: [NotificationMixin],
  props: {
    shop: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      form: this.shop,
      countryOptions: [
        { value: null, text: 'Wybierz kraj', disabled: true },
        { value: 'PL', text: 'Polska' },
        { value: 'UA', text: 'Ukraina' },
      ],
    };
  },

  methods: {
    onSubmit() {
      this.$emit('onSubmit', this.form);
    },

    showCategoryModal() {
      this.$bvModal.msgBoxOk('Kategorie sklepów określają gdzie sklep będzie widoczny w systemie:\n\n' +
        'Aby sklepy pojawiły się w SKLEPY STACJONARNE:\n' +
        'SKLEP INSTALACYJNY, SKLEP ŁAZIENKOWY, SKLEP INTERNETOWY, HURTOWNIA INSTALACYJNA, DYSTRYBUTOR\n\n' +
        'Aby sklepy pojawiły się w Sieć sprzedaży:\n' +
        'MINI-MARKET BUDOWLANY, HURTOWNIA BUDOWLANA\n\n' +
        'Salony łazienkowe:\n' +
        'SALON ŁAZIENKOWY', {
        title: 'Jak działają kategorie?',
        okVariant: 'success',
        okTitle: 'OK',
        hideHeaderClose: false,
        centered: true,
        size: 'md',
      })
    },

    async tryToGetLocation() {
      const query = `${this.form.address_city}, ${this.form.address_street}, ${this.form.address_number}, ${this.form.postal_code}`;
      const encodedQuery = encodeURIComponent(query);

      try {
        const response = await getLocation(encodedQuery);

        if (response.data.features.length > 0) {
          const feature = response.data.features[0];
          const location = feature.geometry.coordinates;

          this.form.longitude = location[0];
          this.form.latitude = location[1];
        }

        this.showSuccessNotification('Jeśli w systemie jest poprawna lokalizacja, została automatycznie ustawiona.');
      } catch (error) {
        console.log(error);
        this.showErrorNotification('Wystąpił błąd podczas pobierania lokalizacji');
      }
    },

    validationForm() {
      this.$refs.shopObserver.validate().then((success) => {
        if (success) {
          return this.onSubmit();
        }

        this.showFormValidationNotification();
      });
    },
  },
};
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';

.form-container {
  .card-header {
    background-color: rgba(115, 103, 240, 0.12);
  }

  .section-header {
    color: #7367f0;
    font-weight: 600;
    border-bottom: 1px solid #ebe9f1;
    padding-bottom: 0.5rem;
  }
}
</style>
